import { create } from 'zustand';
import errorHandler from '../utils/error-handler.util';
import { IApiResponse } from '../models/resource.model';
import { IGetBusiness } from '../models/business.model';
import useOnboardingStore from './onboarding';
import createAxiosInstance from '../services/global/axiosInstance';
import { IGetUltimateBeneficiary } from '../models/onboarding.model';
import useRateStore from './rate';

const axiosInstance = createAxiosInstance();

interface IBusinessStore {
  businessTypes: string[];
  business: IGetBusiness | null;
  loading: boolean;
  getBusinessTypes: () => Promise<IApiResponse>;
  getBusiness: () => Promise<IApiResponse>;
}

const useBusinessStore = create<IBusinessStore>((set) => ({
  businessTypes: [],
  business: null,
  loading: false,
  getBusinessTypes: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get('/corporate/businessType');
      set({
        businessTypes: response?.data.value?.value,
      });
      set({ loading: false });
      return response?.data.value;
    } catch (err: any) {
      set({ loading: false });
      errorHandler(err);
    }
  },
  getBusiness: async () => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get('/corporate/business');
      const businessData: IGetBusiness = response?.data.value?.value;

      set({
        business: businessData,
      });
      useOnboardingStore.getState().setBusinessInfo({
        email: businessData.email,
        description: businessData.description,
        website: businessData.website,
        registrationNumber: businessData.registrationNumber,
        businessType: businessData.businessType,
      });

      if (businessData.address) {
        useOnboardingStore.getState().setBusinessAddress({
          streetLine1: businessData.address?.streetLine1,
          streetLine2: businessData.address?.streetLine2,
          city: businessData.address?.city,
          state: businessData.address?.state,
          postalCode: businessData.address?.postalCode,
          countryCode: businessData.address?.countryCode,
          countryName: businessData.address?.countryName,
          countryId: businessData?.countryId,
        });
      }

      if (
        businessData.ultimateBeneficialOwners &&
        businessData.ultimateBeneficialOwners.length
      ) {
        useOnboardingStore.getState().setAllUltimateBeneficiaries(
          businessData.ultimateBeneficialOwners.map(
            (owner: IGetUltimateBeneficiary) => {
              const { activeCountries: countries } = useRateStore.getState();
              const idIssuedCountry = countries.find(
                (item) => item.id === owner.idIssuedCountryId,
              );

              return {
                id: owner.id || null,
                firstName: owner.firstName,
                lastName: owner.lastName,
                birthDate: owner.birthDate,
                email: owner.email,
                phone: owner.phone,
                identityType: owner.identityTypeId,
                countryId: owner.countryId,
                open: false,

                // Identity
                idIssuingCountryCode: idIssuedCountry
                  ? idIssuedCountry.iso3
                  : '',
                idIssuedCountryId: owner.idIssuedCountryId,
                identityNumberType: owner.identity.identityNumberType,
                identityNumber: owner.identity.identityNumber,
                identityDocumentType: owner.identity.identityDocumentType,
                idIssuingCountryCodeTouched: false,
                identityNumberTypeTouched: false,
                identityDocumentTypeTouched: false,

                // Address
                streetLine1: owner.address.streetLine1,
                streetLine2: owner.address.streetLine2 || '',
                city: owner.address.city,
                state: owner.address.state,
                postalCode: owner.address.postalCode,
                countryCode: owner.address.countryCode,
                countryName: owner.countryName,
                stateTouched: false,
                countryCodeTouched: false,

                // Documents
                identityDocumentFrontId: owner.docs.frontDocument,
                identityDocumentBackId: owner.docs.backDocument,
              };
            },
          ),
        );
      }

      // useOnboardingStore.getState().setBusinessQuestionnaire({
      //   sourceOfFund: businessData.questionnaire.sourceOfFund,
      //   transmitsCustomerFunds:
      //     businessData.questionnaire.transmitsCustomerFunds,
      //   estimatedAnnualRevenueUsd:
      //     businessData.questionnaire.estimatedAnnualRevenueUsd,
      //   estimatedMonthlyPayOut:
      //     businessData.questionnaire.estimatedMonthlyPayOut,
      //   sourceOfFundDescription:
      //     businessData.questionnaire.sourceOfFundDescription,
      // });
      set({ loading: false });
      return response?.data.value;
    } catch (err: any) {
      set({ loading: false });
      errorHandler(err);
    }
  },
}));

export default useBusinessStore;
